import React from 'react';
import {Helmet} from 'react-helmet';
import Navbar from '../../../components/Navbars/Navbar2';
import {Button, Card, Table, Collapse} from 'antd';
import {permColors} from '../../../utils/colors';
import {CheckCircleOutlined, GlobalOutlined, CheckOutlined, ClockCircleOutlined, DollarOutlined} from '@ant-design/icons';
import Footer from '../../../components/Footer/Footer';
import {ROUTES} from '../../../utils/ROUTES';
import {useNavigate} from 'react-router-dom';
import TrustedBy from '../../../components/single-role-components/TrustedBy';
import Testimonials from '../../../components/single-role-components/Testimonials';
import {ButtonHrContactDrawer} from '../../../components/hr-components/HrContactPopup';
import AnimatedBackground from '../../../components/Backgrounds/AnimatedBackground';

const {Panel} = Collapse;

// Hero Section Component
const HeroSection = () => {
	const benefits = [
		'Hire Philippine employees & contractors quickly and compliantly',
		'No local entity setup required',
		'Automated payroll, benefits, and tax compliance',
		'Streamlined contractor payments',
		'2-3 day onboarding',
		'Full HR support included',
	];

	return (
		<div className="w-full flex flex-col justify-center bg-permhunt">
			<Navbar darkMode={true} />

			<Card className="w-[1240px] max-w-full p-3 px-1 py-10 text-left bg-transparent border-none mx-auto">
				<div className="flex flex-col lg:flex-row justify-center items-center gap-12">
					<div className="left min-w-[300px] max-w-[550px]">
						<h1 className="mb-3 text-4xl font-medium" style={{lineHeight: '1.3', color: permColors.white}}>
							Hire Philippine employees & contractors, hassle-free
						</h1>

						<p className="mb-6 text-lg opacity-90" style={{color: permColors.white}}>
							Permworks HR's Employer of Record (EOR) and contractor services let you hire Philippine talent without local entity setup. We handle compliance, payroll, and taxes.
						</p>

						<div className="flex flex-col gap-2 my-4">
							{benefits.map((benefit: string, i: number) => (
								<p key={i} className="flex gap-3 items-start max-w-full opacity-90 text-base font-light" style={{color: permColors.white}}>
									<CheckCircleOutlined className="mt-1" />
									<span>{benefit}</span>
								</p>
							))}
						</div>

						<ButtonHrContactDrawer
							text="Request a Demo"
							className="h-[50px] w-full max-w-[300px] bg-white/90 text-deepBlue hover:bg-backgroundGreyBlue/20 border-white/20 hover:text-white border-1 mt-6 mb-2 font-semibold text-lg"
						/>
						<p className="text-white/80 text-sm font-light italic">Get started in 24 hours</p>
					</div>
					<img src="/images/initial-section/social-media-manager.png" alt="Philippine Remote Team" className="rounded-lg object-cover w-[400px] mb-[-64px] lg:mb-[-230px]" />
				</div>
			</Card>
		</div>
	);
};

// Key Benefits Section
const KeyBenefitsSection = () => {
	const benefits = [
		{
			icon: <GlobalOutlined className="text-3xl text-antBlue" />,
			title: 'Global Compliance',
			description: 'Stay compliant with Philippine labor laws, tax regulations, and employment requirements for both employees and contractors without the complexity.',
		},
		{
			icon: <ClockCircleOutlined className="text-3xl text-antBlue" />,
			title: 'Fast Onboarding',
			description: 'Get your Philippine employees and contractors onboarded in 2-3 business days with our streamlined process.',
		},
		{
			icon: <CheckOutlined className="text-3xl text-antBlue" />,
			title: 'Full Legal Protection',
			description: 'We handle all legal responsibilities for employees as the official employer of record, and ensure contractor compliance, protecting your business from liability.',
		},
		{
			icon: <DollarOutlined className="text-3xl text-antBlue" />,
			title: 'Cost Effective',
			description: 'No need to establish a local entity. Save on setup costs, legal fees, and ongoing administrative expenses for both employment and contractor arrangements.',
		},
	];

	return (
		<div className="py-20 px-4 bg-white">
			<div className="max-w-[1200px] mx-auto">
				<h2 className="text-3xl font-normal text-center mb-4 text-darkCharcoal">All the benefits you need for hiring employees & contractors in the Philippines</h2>
				<p className="text-center text-charcoal mb-12 max-w-[600px] mx-auto">
					Permworks HR's EOR and contractor services allow you to hire and manage Philippine talent within minutes, all from one manageable online dashboard.
				</p>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
					{benefits.map((benefit, index) => (
						<div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow">
							<div className="mb-4 flex justify-center">{benefit.icon}</div>
							<h3 className="text-xl font-medium mb-3 text-darkCharcoal">{benefit.title}</h3>
							<p className="text-charcoal text-sm leading-relaxed">{benefit.description}</p>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

// Payroll & Benefits Section
const PayrollBenefitsSection = () => {
	const employeeBenefits = [
		'Philippine Health Insurance Corporation (PhilHealth)',
		'Social Security System (SSS)',
		'Home Development Mutual Fund (HDMF/Pag-IBIG)',
		'Private Healthcare Options',
		'13th Month Pay',
		'Vacation and Sick Leave Management',
		'Maternity/Paternity Leave Compliance',
		'Overtime and Holiday Pay Calculations',
	];

	const contractorFeatures = ['Streamlined contractor payment processing', 'Tax-compliant contractor agreements', 'Automated invoice management', 'BIR compliance for contractor payments'];

	return (
		<div className="py-20 px-4 bg-backgroundGreyBlue">
			<div className="max-w-[1200px] mx-auto">
				<div className="flex flex-col lg:flex-row items-center gap-12">
					<div className="lg:w-1/2">
						<h2 className="text-3xl font-normal mb-6 text-darkCharcoal">Complete payroll, benefits & contractor management</h2>
						<p className="text-charcoal mb-6 leading-relaxed">
							We handle all mandatory Philippine benefits and payroll requirements for employees, plus streamlined contractor payment processing, ensuring compliance with local laws for
							all your Philippine talent.
						</p>

						<div className="mb-6">
							<h3 className="text-lg font-medium mb-3 text-darkCharcoal">Employee Benefits</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
								{employeeBenefits.map((benefit, index) => (
									<div key={index} className="flex items-center gap-2">
										<CheckCircleOutlined className="text-antBlue" />
										<span className="text-sm text-charcoal">{benefit}</span>
									</div>
								))}
							</div>
						</div>

						<div className="mb-6">
							<h3 className="text-lg font-medium mb-3 text-darkCharcoal">Contractor Services</h3>
							<div className="grid grid-cols-1 gap-3">
								{contractorFeatures.map((feature, index) => (
									<div key={index} className="flex items-center gap-2">
										<CheckCircleOutlined className="text-antBlue" />
										<span className="text-sm text-charcoal">{feature}</span>
									</div>
								))}
							</div>
						</div>

						<ButtonHrContactDrawer text="Learn More About Our Services" className="mt-8 h-[45px] px-8" />
					</div>

					<div className="lg:w-1/2">
						<img src="/images/hr/hr-landing/dashboard.png" alt="Payroll Management Dashboard" className="w-full rounded-lg shadow-lg" />
					</div>
				</div>
			</div>
		</div>
	);
};

// Compliance Table Section
const ComplianceSection = () => {
	const complianceData = [
		{
			key: '1',
			requirement: 'Employee Wage Compliance',
			permworks: '✓ Automated for employees',
			traditional: '✗ Manual tracking required',
		},
		{
			key: '2',
			requirement: 'Tax Withholding & Filing',
			permworks: '✓ Fully managed for employees & contractors',
			traditional: '✗ Complex setup needed',
		},
		{
			key: '3',
			requirement: 'Benefits Administration',
			permworks: '✓ All employee benefits included',
			traditional: '✗ Separate setup required',
		},
		{
			key: '4',
			requirement: 'Employment & Contractor Agreements',
			permworks: '✓ Legally compliant templates for both',
			traditional: '✗ Legal review needed',
		},
		{
			key: '5',
			requirement: 'Termination & Contract End Procedures',
			permworks: '✓ Handled professionally for both',
			traditional: '✗ Risk of non-compliance',
		},
	];

	const columns = [
		{
			title: 'Compliance Requirement',
			dataIndex: 'requirement',
			key: 'requirement',
			width: '40%',
		},
		{
			title: 'With Permworks HR',
			dataIndex: 'permworks',
			key: 'permworks',
			width: '30%',
			className: 'permworks-column',
			onCell: () => ({
				style: {
					backgroundColor: permColors.backgroundGreyBlue,
					fontWeight: 'bold',
					color: permColors.deepBlue,
				},
			}),
			onHeaderCell: () => ({
				style: {
					backgroundColor: permColors.deepBlue,
					color: 'white',
					fontWeight: 'bold',
				},
			}),
		},
		{
			title: 'Traditional Setup',
			dataIndex: 'traditional',
			key: 'traditional',
			width: '30%',
		},
	];

	return (
		<div className="py-20 px-4 bg-white">
			<div className="max-w-[1200px] mx-auto">
				<h2 className="text-3xl font-normal text-center mb-4 text-darkCharcoal">Why choose Permworks HR's EOR & contractor services?</h2>
				<p className="text-center text-charcoal mb-12 max-w-[600px] mx-auto">Compare the complexity of traditional hiring vs. our streamlined solutions for both employees and contractors</p>

				<Table columns={columns} dataSource={complianceData} pagination={false} bordered className="w-full" />
			</div>
		</div>
	);
};

// How It Works Section
const HowItWorksSection = () => {
	const steps = [
		{
			number: '1',
			title: 'Initial Consultation',
			description: "Tell us about your hiring needs and we'll create a customized solution for employees or contractors with Permworks HR.",
			image: '/images/eor/consultation.png',
		},
		{
			number: '2',
			title: 'Talent Onboarding',
			description: 'We handle all paperwork, contracts, and legal requirements to get your employees or contractors started quickly.',
			image: '/images/eor/onboarding.png',
		},
		{
			number: '3',
			title: 'Ongoing Management',
			description: 'Permworks HR manages payroll, benefits, compliance, and payments while you focus on your business.',
			image: '/images/eor/management.png',
		},
	];

	return (
		<div className="py-20 px-4 bg-backgroundGreyBlue">
			<div className="max-w-[1200px] mx-auto">
				<h2 className="text-3xl font-normal text-center mb-4 text-darkCharcoal">How Permworks HR's services work</h2>
				<p className="text-center text-charcoal mb-16 max-w-[600px] mx-auto">Get started with Philippine employees or contractors in just 3 simple steps</p>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-12">
					{steps.map((step, index) => (
						<div key={index} className="text-center">
							<div className="w-16 h-16 bg-antBlue text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">{step.number}</div>
							{/* <img 
                src={step.image} 
                alt={step.title}
                className="w-full max-w-[300px] mx-auto mb-6 rounded-lg"
              /> */}
							<h3 className="text-xl font-medium mb-3 text-darkCharcoal">{step.title}</h3>
							<p className="text-charcoal leading-relaxed">{step.description}</p>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

// Pricing Section
const PricingSection = () => {
	return (
		<div className="py-20 px-4 bg-white">
			<div className="max-w-[1000px] mx-auto text-center">
				<h2 className="text-3xl font-normal mb-6 text-darkCharcoal">Transparent, competitive pricing</h2>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
					{/* EOR Pricing */}
					<Card className="p-8">
						<div className="text-2xl font-bold text-antBlue mb-2">EOR Service</div>
						<div className="text-3xl font-bold text-antBlue mb-2">$299 USD</div>
						<div className="text-lg text-charcoal mb-4">Monthly management fee per employee</div>
						<div className="text-sm text-charcoal mb-6">+ ~12.5% employer costs (taxes & benefits)</div>

						<div className="text-left max-w-[400px] mx-auto">
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Full payroll management</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">All mandatory benefits included</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Legal compliance guaranteed</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Dedicated HR support</span>
							</div>
							<div className="flex items-center gap-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">No setup or termination fees</span>
							</div>
						</div>
					</Card>

					{/* Contractor Service Pricing */}
					<Card className="p-8">
						<div className="text-2xl font-bold text-antBlue mb-2">Contractor Service</div>
						<div className="text-3xl font-bold text-antBlue mb-2">$99 USD</div>
						<div className="text-lg text-charcoal mb-4">Monthly management fee per contractor</div>
						<div className="text-sm text-charcoal mb-6">Includes payment processing & compliance</div>

						<div className="text-left max-w-[400px] mx-auto">
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Streamlined payment processing</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Tax-compliant agreements</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">Automated invoice management</span>
							</div>
							<div className="flex items-center gap-2 mb-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">BIR compliance support</span>
							</div>
							<div className="flex items-center gap-2">
								<CheckCircleOutlined className="text-antBlue" />
								<span className="text-sm">No setup fees</span>
							</div>
						</div>
					</Card>
				</div>

				<ButtonHrContactDrawer text="Get Started Today" className="h-[50px] px-12 text-lg font-semibold" />
			</div>
		</div>
	);
};

// FAQ Section
const FaqSection = () => {
	const faqs = [
		{
			question: 'What services does Permworks HR offer?',
			answer: 'Permworks HR offers both Employer of Record (EOR) services for hiring employees and contractor management services. Our EOR service allows you to hire employees in the Philippines without establishing a local entity, while our contractor service helps you manage and pay contractors compliantly.',
		},
		{
			question: "What's the difference between EOR and contractor services?",
			answer: 'EOR service is for hiring full employees where Permworks HR becomes the legal employer, handling all benefits, taxes, and compliance. Contractor service is for managing independent contractors with streamlined payment processing and tax compliance, but without employee benefits.',
		},
		{
			question: 'How quickly can I hire someone through Permworks HR?',
			answer: 'Our average onboarding time is 2-3 business days for both employees and contractors once all required documentation is provided. This includes contract preparation, benefits enrollment (for employees), and payment setup.',
		},
		{
			question: 'What benefits are included for Philippine employees vs contractors?',
			answer: 'Employees receive all mandatory Philippine benefits including PhilHealth, SSS, Pag-IBIG, 13th month pay, vacation leave, sick leave, and maternity/paternity leave. Contractors receive streamlined payment processing and tax compliance support but not employee benefits.',
		},
		{
			question: 'Do I need to have a business entity in the Philippines?',
			answer: "No, that's the main benefit of using Permworks HR's services. For employees, we handle all legal employer responsibilities through our EOR service. For contractors, we ensure compliant contractor agreements without requiring a local entity.",
		},
		{
			question: 'How does payroll work for employees vs contractor payments?',
			answer: 'For employees, we handle the entire payroll process including salary payments, tax withholdings, benefits deductions, and government remittances. For contractors, we process payments according to their invoices while ensuring tax compliance and proper documentation.',
		},
	];

	return (
		<div className="py-20 px-4 bg-backgroundGreyBlue">
			<div className="max-w-[800px] mx-auto">
				<h2 className="text-3xl font-normal text-center mb-12 text-darkCharcoal">Frequently Asked Questions</h2>

				<Collapse ghost>
					{faqs.map((faq, index) => (
						<Panel header={<span className="text-lg font-medium text-darkCharcoal">{faq.question}</span>} key={index} className="mb-4 bg-white rounded-lg">
							<p className="text-charcoal leading-relaxed pl-4">{faq.answer}</p>
						</Panel>
					))}
				</Collapse>
			</div>
		</div>
	);
};

// CTA Section
const CtaSection = () => {
	const navigate = useNavigate();

	return (
		<div className="py-20 px-4 bg-deepBlue">
			<div className="max-w-[800px] mx-auto text-center">
				<h2 className="text-3xl font-normal mb-6 text-white">Ready to hire your Philippine team?</h2>
				<p className="text-lg text-white/90 mb-8 leading-relaxed">
					Join hundreds of companies who trust Permworks HR's EOR and contractor services to manage their Philippine talent. Get started today and have your team onboarded within days.
				</p>

				<div className="flex flex-col sm:flex-row gap-4 justify-center">
					<ButtonHrContactDrawer text="Request a Demo" className="h-[50px] px-8 bg-white text-deepBlue hover:bg-gray-100 border-none font-semibold" />
					<Button
						type="default"
						size="large"
						className="h-[50px] px-8 bg-transparent text-white border-white hover:bg-white/10 font-semibold"
						onClick={() => navigate(ROUTES.HR_PAGES.HR_PRICING)}>
						View Pricing
					</Button>
				</div>

				<p className="text-white/70 text-sm mt-4">No setup fees • No long-term contracts • Get started in 24 hours</p>
			</div>
		</div>
	);
};

// Main EOR Landing Page Component
export default function HrLandingPage() {
	// SEO structured data for the page
	const structuredData = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Permworks HR',
		description: 'Employer of Record (EOR) and contractor services for hiring Philippine talent without local entity setup',
		url: 'https://permworks.com/hr',
		logo: 'https://permworks.com/logo.png',
		contactPoint: {
			'@type': 'ContactPoint',
			telephone: '+1-XXX-XXX-XXXX',
			contactType: 'customer service',
			availableLanguage: ['English'],
		},
		address: {
			'@type': 'PostalAddress',
			addressCountry: 'PH',
			addressRegion: 'Metro Manila',
		},
		sameAs: ['https://www.linkedin.com/company/permworks', 'https://twitter.com/permworks'],
		service: [
			{
				'@type': 'Service',
				name: 'Employer of Record (EOR) Services',
				description: 'Hire Philippine employees without establishing a local entity',
				provider: {
					'@type': 'Organization',
					name: 'Permworks HR',
				},
			},
			{
				'@type': 'Service',
				name: 'Contractor Management Services',
				description: 'Streamlined contractor payment processing and compliance',
				provider: {
					'@type': 'Organization',
					name: 'Permworks HR',
				},
			},
		],
	};

	const faqStructuredData = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: [
			{
				'@type': 'Question',
				name: 'What services does Permworks HR offer?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Permworks HR offers both Employer of Record (EOR) services for hiring employees and contractor management services. Our EOR service allows you to hire employees in the Philippines without establishing a local entity, while our contractor service helps you manage and pay contractors compliantly.',
				},
			},
			{
				'@type': 'Question',
				name: "What's the difference between EOR and contractor services?",
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'EOR service is for hiring full employees where Permworks HR becomes the legal employer, handling all benefits, taxes, and compliance. Contractor service is for managing independent contractors with streamlined payment processing and tax compliance, but without employee benefits.',
				},
			},
			{
				'@type': 'Question',
				name: 'How quickly can I hire someone through Permworks HR?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: 'Our average onboarding time is 2-3 business days for both employees and contractors once all required documentation is provided. This includes contract preparation, benefits enrollment (for employees), and payment setup.',
				},
			},
			{
				'@type': 'Question',
				name: 'Do I need to have a business entity in the Philippines?',
				acceptedAnswer: {
					'@type': 'Answer',
					text: "No, that's the main benefit of using Permworks HR's services. For employees, we handle all legal employer responsibilities through our EOR service. For contractors, we ensure compliant contractor agreements without requiring a local entity.",
				},
			},
		],
	};

	return (
		<div>
			<Helmet>
				{/* Primary Meta Tags */}
				<title>Hire Philippine Employees & Contractors | Permworks HR EOR Services</title>
				<meta
					name="description"
					content="Hire Philippine employees & contractors hassle-free with Permworks HR's Employer of Record (EOR) and contractor services. No local entity setup required. 2-3 day onboarding, full compliance, automated payroll. Get started today."
				/>
				<meta
					name="keywords"
					content="Philippines EOR, Employer of Record Philippines, hire Philippine employees, Philippine contractors, Philippines payroll, Philippine compliance, hire remote workers Philippines, EOR services, contractor management Philippines"
				/>
				<meta name="author" content="Permworks HR" />
				<meta name="robots" content="index, follow" />
				<meta name="viewport" content="width=device-width, initial-scale=1.0" />

				{/* Canonical URL */}
				<link rel="canonical" href="https://permworks.com/hr" />

				{/* Additional Meta Tags */}
				<meta name="theme-color" content="#1890ff" />
				<meta name="msapplication-TileColor" content="#1890ff" />
				<meta name="application-name" content="Permworks HR" />

				{/* Language */}
				<meta httpEquiv="content-language" content="en-US" />

				{/* Preconnect for performance */}
				<link rel="preconnect" href="https://fonts.googleapis.com" />
				<link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />

				{/* Structured Data */}
				<script type="application/ld+json">{JSON.stringify(structuredData)}</script>
				<script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>

				{/* Additional SEO Tags */}
				<meta name="rating" content="general" />
				<meta name="distribution" content="global" />
				<meta name="revisit-after" content="7 days" />
				<meta name="expires" content="never" />

				{/* Business/Contact Information */}
				<meta name="contact" content="hello@permworks.com" />
				<meta name="reply-to" content="hello@permworks.com" />

				{/* Alternate Languages (if you have them) */}
				<link rel="alternate" hrefLang="en" href="https://permworks.com/hr" />
				<link rel="alternate" hrefLang="x-default" href="https://permworks.com/hr" />
			</Helmet>

			<HeroSection />
			<TrustedBy />
			<KeyBenefitsSection />
			<PayrollBenefitsSection />
			<ComplianceSection />
			<HowItWorksSection />
			<PricingSection />
			<Testimonials />
			<FaqSection />
			<CtaSection />
			<Footer backgroundColor={permColors.white} />
		</div>
	);
}
